import {vnTemplate} from "../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/vnTemplate";
import {multipleSelector_template} from "../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/multipleSelector_template";
import {filterDate_template} from "../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/filterDate_template";
import {validationsList_template} from "../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/validationsList_template";
import {presetFilter_template} from "../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/presetFilter_template";
import {exportDataCsv} from "../../../vendor/valnet/valnet_jslib/vnHelpers/exportDataCsv";
import {infoButton_template} from "../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/infoButton_template";

declare var ko: any;

export class carMarketplaceAffiliateReport_template extends vnTemplate{
    protected brandsFilter: multipleSelector_template;
    protected affiliateFilter: multipleSelector_template;
    protected placementFilter: multipleSelector_template;
    protected makeFilter: multipleSelector_template;
    protected spendSourcesFilter: multipleSelector_template;
    protected datesFilter: filterDate_template;
    public validationsList: validationsList_template;
    public presetFilter: presetFilter_template;
    public uniqueClicksTooltip: infoButton_template;
    public monetizedClicksTooltip: infoButton_template;
    protected exportReportDataCsv: exportDataCsv;
    private affiliateColumnDefinitions: Array<any> = [];
    private spendSourceColumnDefinitions: Array<any> = [];
    private providerCompareColumnDefinitions: Array<any> = [];

    private getAffiliateColumnDefinitions() {
        return Array.isArray(this.affiliateColumnDefinitions) ? this.affiliateColumnDefinitions : [];
    }

    private getSpendSourceColumnDefinitions() {
        return Array.isArray(this.spendSourceColumnDefinitions) ? this.spendSourceColumnDefinitions : [];
    }

    private normalizeIdList(ids: Array<any>): Array<string> {
        if (!Array.isArray(ids)) {
            return [];
        }

        const seen = {};
        const normalized: Array<string> = [];
        for (const id of ids) {
            const value = String(id ?? '').trim().toLowerCase();
            if (!value || seen[value]) {
                continue;
            }

            seen[value] = true;
            normalized.push(value);
        }

        return normalized;
    }

    private normalizeAffiliateColumnDefinitions(items: Array<any>) {
        if (!Array.isArray(items)) {
            return [];
        }

        return items
            .filter((item) => item && typeof item.id !== 'undefined' && typeof item.name !== 'undefined')
            .map((item) => {
                // Keep UI partner-agnostic: backend sends id/name/columnKey/rate/subLabel.
                const rate = Number(item.rate);
                const fallbackSubLabel = Number.isFinite(rate) ? `$${rate.toFixed(2)}` : '';
                return {
                    id: String(item.id ?? '').trim().toLowerCase(),
                    key: item.columnKey,
                    label: item.name,
                    subLabel: item.subLabel || fallbackSubLabel,
                };
            })
            .filter((definition) => typeof definition.key === 'string' && definition.key.length > 0);
    }

    /** Maps backend spend source metadata (id, name, columnKey) into Spend section column defs. */
    private normalizeSpendSourceColumnDefinitions(items: Array<any>) {
        if (!Array.isArray(items)) {
            return [];
        }

        return items
            .filter((item) => item && typeof item.id !== 'undefined' && typeof item.name !== 'undefined')
            .map((item) => ({
                id: String(item.id ?? '').trim().toLowerCase(),
                key: item.columnKey,
                label: item.name,
            }))
            .filter((definition) => typeof definition.key === 'string' && definition.key.length > 0);
    }

    private getProviderCompareColumnDefinitions() {
        return Array.isArray(this.providerCompareColumnDefinitions) ? this.providerCompareColumnDefinitions : [];
    }

    private normalizeProviderCompareColumnDefinitions(items: Array<any>) {
        if (!Array.isArray(items)) {
            return [];
        }

        // Provider compare columns are backend-driven so frontend stays provider-agnostic.
        return items
            .filter((item) => item && typeof item.id !== 'undefined' && typeof item.name !== 'undefined')
            .map((item) => ({
                id: String(item.id ?? '').trim().toLowerCase(),
                label: item.name,
                clicksKey: item.clicksColumnKey,
                revenueKey: item.revenueColumnKey,
            }))
            .filter((definition) =>
                typeof definition.clicksKey === 'string'
                && definition.clicksKey.length > 0
                && typeof definition.revenueKey === 'string'
                && definition.revenueKey.length > 0
            );
    }

    private getDateMetricColumns() {
        return [
            { key: 'roas', label: 'ROAS', type: 'decimal', sortable: true, headerClass: 'align-right', cellClass: 'align-right', group: 'metrics' },
            { key: 'margin', label: 'Margin', type: 'percent2', sortable: true, headerClass: 'align-right', cellClass: 'align-right', group: 'metrics' },
            { key: 'cpc', label: 'CPC', type: 'currencyNullable', sortable: true, headerClass: 'align-right', cellClass: 'align-right', group: 'metrics' },
            { key: 'pageViews', label: 'Pageviews', type: 'number', sortable: true, headerClass: 'align-right', cellClass: 'align-right', group: 'metrics' },
            { key: 'sessions', label: 'Sessions', type: 'number', sortable: true, headerClass: 'align-right', cellClass: 'align-right', group: 'metrics' },
            { key: 'clicks', label: 'Clicks', type: 'number', sortable: true, headerClass: 'align-right', cellClass: 'align-right', group: 'metrics' },
            { key: 'uniqueClicks', label: 'Unique Clicks', type: 'number', sortable: true, headerClass: 'align-right car-marketplace-tooltip-col', cellClass: 'align-right', group: 'metrics' },
            { key: 'monetizedClicks', label: 'Monetized Clicks', type: 'number', sortable: true, headerClass: 'align-right car-marketplace-tooltip-col', cellClass: 'align-right', group: 'metrics' },
            { key: 'organicPct', label: 'Organic %', type: 'percent2', sortable: true, headerClass: 'align-right', cellClass: 'align-right', group: 'metrics' },
            { key: 'ctr', label: 'CTR %', type: 'percent2', sortable: true, headerClass: 'align-right', cellClass: 'align-right', group: 'metrics' },
            { key: 'clickRevenue', label: 'Click Rev', type: 'currency', sortable: true, headerClass: 'align-right', cellClass: 'align-right', group: 'metrics' },
            { key: 'totalRevenue', label: 'Total Rev', type: 'currency', sortable: true, headerClass: 'align-right border-right', cellClass: 'align-right border-right', group: 'metrics' },
        ];
    }

    /** Net sits between affiliate total and spend; Total Rev stays in metrics. */
    private getDateNetColumn() {
        return {
            key: 'net',
            label: 'Net',
            type: 'currency',
            sortable: true,
            headerClass: 'colgroup-4 expanded-cells total align-right border-right',
            cellClass: 'colgroup-4 expanded-cells total align-right border-right',
            group: 'net',
        };
    }

    private getSpendColumns(selectedSpendSources: Array<string> = []) {
        const definitions = this.getSpendSourceColumnDefinitions();
        const normalized = this.normalizeIdList(selectedSpendSources);
        const selectedDefinitions = normalized.length === 0
            ? definitions
            : definitions.filter((definition) => normalized.includes(definition.id));

        const sourceColumns = selectedDefinitions.map((definition) => ({
            key: definition.key,
            label: definition.label,
            type: 'currency',
            sortable: true,
            headerClass: 'align-right',
            cellClass: 'align-right',
            group: 'spend',
        }));

        return [
            ...sourceColumns,
            { key: 'spendTotal', label: 'Total', type: 'currency', sortable: true, headerClass: 'align-right border-right section-2 total', cellClass: 'align-right border-right section-2 total', group: 'spend' },
        ];
    }

    private getMakeMetricColumns() {
        return [
            { key: 'clicks', label: 'Total clicks', type: 'number', sortable: true, headerClass: 'section-1 align-right', cellClass: 'section-1 align-right', group: 'metrics' },
            { key: 'monetizedClicks', label: 'Monetized clicks', type: 'number', sortable: true, headerClass: 'section-1 align-right', cellClass: 'section-1 align-right', group: 'metrics' },
            { key: 'totalRevenue', label: 'Revenue', type: 'currency', sortable: true, headerClass: 'section-1 align-right section-department', cellClass: 'section-1 align-right section-department total', group: 'metrics' },
        ];
    }

    private getProviderCompareColumns(selectedAffiliates: Array<string>) {
        const definitions = this.getProviderCompareColumnDefinitions();
        const normalized = this.normalizeIdList(selectedAffiliates);
        // Mirror affiliate filter behavior: only show compare columns for selected affiliates.
        const selectedDefinitions = normalized.length === 0
            ? definitions
            : definitions.filter((definition) => normalized.includes(definition.id));
        const columns = [];

        for (const definition of selectedDefinitions) {
            columns.push({
                key: definition.clicksKey,
                label: `${definition.label} Clicks`,
                type: 'numberNullable',
                sortable: true,
                headerClass: 'align-right expanded-cells',
                cellClass: 'align-right expanded-cells',
                group: 'providerCompare',
            });
            columns.push({
                key: definition.revenueKey,
                label: `${definition.label} Revenue`,
                type: 'currencyNullable',
                sortable: true,
                headerClass: 'align-right expanded-cells',
                cellClass: 'align-right expanded-cells',
                group: 'providerCompare',
            });
        }

        if (selectedDefinitions.length > 0) {
            // Totals are computed by backend and rendered as dedicated compare columns.
            columns.push({
                key: 'providerCompareTotalClicks',
                label: 'Total Clicks',
                type: 'numberNullable',
                sortable: true,
                headerClass: 'colgroup-4 expanded-cells total align-right',
                cellClass: 'section-4 border-right total align-right',
                group: 'providerCompare',
            });
            columns.push({
                key: 'providerCompareTotalRevenue',
                label: 'Total Revenue',
                type: 'currencyNullable',
                sortable: true,
                headerClass: 'colgroup-4 expanded-cells total align-right',
                cellClass: 'section-4 border-right total align-right',
                group: 'providerCompare',
            });
        }

        return columns;
    }

    private buildDateViewColumns(selectedAffiliates: Array<string>, selectedSpendSources: Array<string>, includeProviderCompare: boolean = false) {
        const affiliateDefinitions = this.getAffiliateColumnDefinitions();
        const normalized = this.normalizeIdList(selectedAffiliates);
        const metricColumns = this.getDateMetricColumns().map((column) => ({ ...column }));

        // No affiliate selected => show all configured affiliate columns.
        const selectedDefinitions = normalized.length === 0
            ? affiliateDefinitions
            : affiliateDefinitions.filter((definition) => normalized.includes(definition.id));

        const affiliateColumns = selectedDefinitions.map((definition) => ({
            key: definition.key,
            label: definition.label,
            subLabel: definition.subLabel,
            type: 'currency',
            sortable: true,
            headerClass: 'align-right',
            cellClass: 'align-right',
            group: 'affiliate',
        }));

        if (includeProviderCompare && metricColumns.length > 0) {
            // Add visual split between core metrics and comparison section when expanded.
            const lastMetricColumn = metricColumns[metricColumns.length - 1];
            if (!lastMetricColumn.headerClass.includes('border-right')) {
                lastMetricColumn.headerClass = `${lastMetricColumn.headerClass} border-right`;
                lastMetricColumn.cellClass = `${lastMetricColumn.cellClass} border-right`;
            }
        }

        const spendColumns = this.getSpendColumns(selectedSpendSources);

        return [
            { key: 'date', label: 'Date', type: 'date', sortable: true, headerClass: 'border-right', cellClass: 'align-right border-right', group: 'date' },
            ...affiliateColumns,
            { key: 'directRevenue', label: 'Direct Revenue', type: 'currency', sortable: true, headerClass: 'align-right car-marketplace-direct-revenue', cellClass: 'align-right car-marketplace-direct-revenue', group: 'directRevenue' },
            { key: 'affiliateTotalRevenue', label: 'Total', type: 'currency', sortable: true, headerClass: 'colgroup-4 expanded-cells total align-right', cellClass: 'colgroup-4 expanded-cells total align-right', group: 'total' },
            this.getDateNetColumn(),
            ...spendColumns,
            ...metricColumns,
            ...(includeProviderCompare ? this.getProviderCompareColumns(normalized) : []),
        ];
    }

    private buildMakeViewColumns() {
        return [
            { key: 'make', label: 'Make', type: 'text', sortable: true, headerClass: 'section-2', cellClass: 'section-2 car-marketplace-dimension', group: 'dimension' },
            ...this.getMakeMetricColumns(),
        ];
    }

    private buildMakeModelViewColumns() {
        return [
            { key: 'make', label: 'Make', type: 'text', sortable: true, headerClass: 'section-2', cellClass: 'section-2 car-marketplace-dimension', group: 'dimension' },
            { key: 'model', label: 'Model', type: 'text', sortable: true, headerClass: 'section-2', cellClass: 'section-2 car-marketplace-dimension', group: 'dimension' },
            ...this.getMakeMetricColumns(),
        ];
    }

    private isDateViewMode(): boolean {
        return this.getSelectedViewMode() === 'date';
    }

    private getSelectedViewMode(): string {
        const selected = this.getTemplateViewModel().viewModeSelected ? this.getTemplateViewModel().viewModeSelected() : 'date';
        return ['date', 'make', 'make_model'].includes(selected) ? selected : 'date';
    }

    private getViewModes(): Array<{ id: string, name: string }> {
        return [
            { id: 'date', name: 'Date' },
            { id: 'make', name: 'Make' },
            { id: 'make_model', name: 'Make and Model' },
        ];
    }

    protected getTemplateHtml(): string{
        return  `
            <div class="sub-header w-justify">

                ` + this.presetFilter.getHtmlBuild() + `

                <div class="w-left">
                    <div class="dropdown select-single">
                        <label class="form-label" for="">View Mode</label>
                        <button class="dropdown-toggle" type="button" data-bind="text: viewModeSelectedText"></button>
                        <div class="dropdown-list">
                            <div class="w-form">
                                <div class="form-group" data-bind="foreach: viewModes">
                                    <input type="radio" data-bind="checked : $parent.viewModeSelected, value: id, attr: { 'id': 'view_mode_' + id, 'name' : 'view_mode' }, event:{ change: $parent.onViewModeChange}">
                                    <label class="form-label" data-bind="attr: {for : 'view_mode_' + id }, text: name"></label>
                                </div>
                            </div>
                        </div>
                    </div>
                    ` + this.brandsFilter.getHtmlBuild() + `
                    ` + this.affiliateFilter.getHtmlBuild() + `
                    ` + this.placementFilter.getHtmlBuild() + `
                    <!-- ko if: viewModeSelected() == 'date' -->
                    ` + this.spendSourcesFilter.getHtmlBuild() + `
                    <div>
                        <div class="dropdown select-single">
                            <label class="form-label" for="">Traffic Type</label>
                            <button class="dropdown-toggle" type="button" data-bind="text: trafficTypeSelectedText"></button>
                            <div class="dropdown-list">
                                <div class="w-form">
                                    <div class="form-group" data-bind="foreach: trafficTypes">
                                        <input type="radio" data-bind="checked : $parent.trafficTypeSelected, value: id, attr: { 'id': 'traffic_type_' + id, 'name' : 'traffic_type' }, event:{ change: $parent.onTrafficTypeChange}">
                                        <label class="form-label" data-bind="attr: {for : 'traffic_type_' + id }, text: name"></label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- /ko -->
                    <!-- ko if: viewModeSelected() == 'make' || viewModeSelected() == 'make_model' -->
                    ` + this.makeFilter.getHtmlBuild() + `
                    <!-- /ko -->
                    ` + this.datesFilter.getHtmlBuild() + `
                </div>
            </div>

            ` + this.validationsList.getHtmlBuild()  + `

             <div class="w-filters-selected">
                <div id="filters-selected" class="filters-selected">
                   <div id="selected-brands"></div>
                   <div id="selected-affiliates"></div>
                   <div id="selected-placements"></div>
                   <!-- ko if: viewModeSelected() == 'date' -->
                   <div id="selected-spend-sources"></div>
                   <!-- /ko -->
                   <div id="selected-makes" data-bind="visible: viewModeSelected() == 'make' || viewModeSelected() == 'make_model'"></div>
                </div>
            </div>

            <div class="list-header w-justify">
                <div class="w-left">
                    <h3 class="section-title icon i-affiliation">
                        <span class="leadgen-section-title-row">Marketplace Report</span>
                    </h3>
                </div>
                <div class="w-right">
                    <button class="btn highlight icon fix-width i-expand" type="button" data-bind="class: collapseButtonClass(), text: collapseButtonText(), css: {'is-hidden': exportButtonHidden() || viewModeSelected() != 'date' }, event: { click: onCollapseClick }">Expand</button>
                    <button class="btn icon highlight i-export" type="button" data-bind="event: { click: exportData }, css: { 'is-hidden': exportButtonHidden(), 'is-loading': exportDataButtonClickable() == false }">Export</button>
                </div>
            </div>

            <div id="list-results" style="position: relative">
                <!-- ko if: viewModeSelected() == 'date' -->
                <div class="w-list-results">
                <table class="list-results list-sections">
                    <thead>
                        <tr class="list-header-section">
                            <th></th>
                            <th data-bind="attr: { colspan: affiliateColumnCount }" class="section-3"><span>AFFILIATE</span></th>
                            <th class="car-marketplace-direct-revenue"></th>
                            <th class="colgroup-4 expanded-cells total"></th>
                            <th class="colgroup-4 expanded-cells total"></th>
                            <th data-bind="attr: { colspan: spendColumnCount }" class="section-2"><span>SPEND</span></th>
                            <th data-bind="attr: { colspan: metricColumnCount }" class="section-1"><span>METRICS</span></th>
                            <!-- ko if: providerCompareColumnCount() > 0 -->
                                <th data-bind="attr: { colspan: providerCompareColumnCount }" class="colgroup-4 expanded-cells align-right"><span>AFFILIATE COMPARISON</span></th>
                            <!-- /ko -->
                        </tr>
                        <tr class="list-header-section">
                            <!-- ko foreach: columns -->
                                <th data-bind="css: headerClass">
                                    <!-- ko if: sortable -->
                                        <div style="display: flex;align-items: center; padding: 0;position: relative;justify-content: flex-end;">
                                            <span class="icon list-sort-none"
                                                  data-bind="text: label, event: { click: $parent.onClickReorder.bind($data, key) }, css: { 'list-sort-desc': $parent.orderByAndDirection()== key + '_DESC', 'list-sort-asc': $parent.orderByAndDirection()== key + '_ASC' }"></span>
                                            <!-- ko if: key === 'uniqueClicks' -->
                                                <div class="w-tooltips-adv auto-height short pos-last car-marketplace-tooltip">
                                                    <!-- ko with: $parent -->
                                                        ` + this.uniqueClicksTooltip.getHtmlBuild() + `
                                                    <!-- /ko -->
                                                </div>
                                            <!-- /ko -->
                                            <!-- ko if: key === 'monetizedClicks' -->
                                                <div class="w-tooltips-adv large pos-right car-marketplace-tooltip car-marketplace-tooltip-wide">
                                                    <!-- ko with: $parent -->
                                                        ` + this.monetizedClicksTooltip.getHtmlBuild() + `
                                                    <!-- /ko -->
                                                </div>
                                            <!-- /ko -->
                                        </div>
                                    <!-- /ko -->
                                    <!-- ko ifnot: sortable -->
                                        <span data-bind="text: label"></span>
                                    <!-- /ko -->
                                    <!-- ko if: $data.subLabel -->
                                        <br>
                                        <span data-bind="text: $data.subLabel"></span>
                                    <!-- /ko -->
                                </th>
                            <!-- /ko -->
                        </tr>
                    </thead>
                    <tbody>
                        <!-- ko foreach: { data: reportData, as: 'row' } -->
                        <tr data-bind="event: { click: $parent.onClickRowClick.bind($data, $index(), $data) }, css: { total: row.isTotal, 'is-clicked': $parent.selectedRows().indexOf($index()) !== -1 }">
                            <!-- ko foreach: $parent.columns -->
                                <td data-bind="css: $data.cellClass">
                                    <span data-bind="text: $parents[1].formatData(row[$data.key], $data.type)"></span>
                                </td>
                            <!-- /ko -->
                        </tr>
                        <!-- /ko -->
                    </tbody>
                </table>
                </div>
                <!-- /ko -->

                <!-- ko if: viewModeSelected() != 'date' -->
                <div class="w-sticky-col car-marketplace-rev-wrap" data-bind="css: { 'is-make-view': viewModeSelected() == 'make', 'is-make-model-view': viewModeSelected() == 'make_model' }">
                    <table class="list-results list-sections car-marketplace-rev-table">
                        <thead>
                            <tr class="list-header-section">
                                <th class="section-1"
                                    data-bind="attr: { colspan: (viewModeSelected() == 'make_model' || viewModeSelected() == 'make') ? 4 : 3 }"><span>METRICS</span></th>
                            </tr>
                            <tr class="list-header-section">
                                <th class="align-right"
                                    data-bind="attr: { colspan: (viewModeSelected() == 'make_model' || viewModeSelected() == 'make') ? 2 : 1 }">
                                    <div style="display: flex;align-items: center; padding: 0;position: relative;justify-content: flex-end;">
                                        <span class="icon list-sort-none"
                                              data-bind="event: { click: onClickReorder.bind($data, 'clicks') }, css: { 'list-sort-desc': orderByAndDirection()== 'clicks_DESC', 'list-sort-asc': orderByAndDirection()== 'clicks_ASC' }">TOTAL CLICKS</span>
                                    </div>
                                </th>
                                <th class="align-right">
                                    <div style="display: flex;align-items: center; padding: 0;position: relative;justify-content: flex-end;">
                                        <span class="icon list-sort-none"
                                              data-bind="event: { click: onClickReorder.bind($data, 'monetizedClicks') }, css: { 'list-sort-desc': orderByAndDirection()== 'monetizedClicks_DESC', 'list-sort-asc': orderByAndDirection()== 'monetizedClicks_ASC' }">MONETIZED CLICKS</span>
                                    </div>
                                </th>
                                <th class="align-right">
                                    <div style="display: flex;align-items: center; padding: 0;position: relative;justify-content: flex-end;">
                                        <span class="icon list-sort-none"
                                              data-bind="event: { click: onClickReorder.bind($data, 'totalRevenue') }, css: { 'list-sort-desc': orderByAndDirection()== 'totalRevenue_DESC', 'list-sort-asc': orderByAndDirection()== 'totalRevenue_ASC' }">REVENUE</span>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- ko foreach: { data: reportData, as: 'row' } -->
                            <tr data-bind="event: { click: $parent.onClickRowClick.bind($data, $index(), $data) }, css: {
                                'is-clicked': $parent.selectedRows().indexOf($index()) !== -1,
                                'car-marketplace-row-make-total': row.rowType == 'make_total',
                                'car-marketplace-row-grand-total': row.rowType == 'grand_total'
                            }">
                                <td class="stick-col stick-col-portfolio"
                                    data-bind="css: {
                                        'car-marketplace-row-make-total': row.rowType == 'make_total',
                                        'car-marketplace-row-grand-total total': row.rowType == 'grand_total'
                                    }">
                                    <span data-bind="text: $parent.formatData(row.make, 'text'),
                                                     css: { 'car-marketplace-row-grand-total-label': row.rowType == 'grand_total' }"></span>
                                </td>
                                <!-- ko if: $parent.viewModeSelected() == 'make_model' -->
                                <td class="stick-col stick-col-brand"
                                    data-bind="css: {
                                        'car-marketplace-row-make-total': row.rowType == 'make_total',
                                        'car-marketplace-row-grand-total total': row.rowType == 'grand_total'
                                    }">
                                    <span data-bind="text: $parent.formatData(row.model, 'text'),
                                                     css: { 'car-marketplace-row-grand-total-label': row.rowType == 'grand_total' }"></span>
                                </td>
                                <!-- /ko -->
                                <td class="align-right" data-bind="css: { 'car-marketplace-row-grand-total grand-total total': row.rowType == 'grand_total' }"><span data-bind="text: $parent.formatData(row.clicks, 'number')"></span></td>
                                <td class="align-right" data-bind="css: { 'car-marketplace-row-grand-total grand-total total': row.rowType == 'grand_total' }"><span data-bind="text: $parent.formatData(row.monetizedClicks, 'number')"></span></td>
                                <td class="align-right" data-bind="css: { 'car-marketplace-row-grand-total grand-total total': row.rowType == 'grand_total' }"><span data-bind="text: $parent.formatData(row.totalRevenue, 'currency')"></span></td>
                            </tr>
                            <!-- /ko -->
                        </tbody>
                    </table>
                </div>
                <!-- /ko -->
            </div>
            `;
    }

    protected initPartial():void{
        this.initializeToolTips();

        this.presetFilter = new presetFilter_template("presetFilter", this.getTemplateViewModel(), this.caller);
        this.presetFilter.setType('carMarketplaceAffiliateReport');

        this.brandsFilter = new multipleSelector_template("brandsFilter", this.getTemplateViewModel(), this.caller);
        this.brandsFilter.setTagContainerId('selected-brands');
        this.brandsFilter.setTagContainerCaption('Selected brands:');
        this.brandsFilter.setLogoEnabled(true);
        this.brandsFilter.setLabel('Brands');

        this.affiliateFilter = new multipleSelector_template("affiliateFilter", this.getTemplateViewModel(), this.caller);
        this.affiliateFilter.setLabel("Affiliate");
        this.affiliateFilter.setFilterContainerClass('margin-neg');
        this.affiliateFilter.setTagContainerId('selected-affiliates');
        this.affiliateFilter.setTagContainerCaption('Selected affiliates:');

        this.placementFilter = new multipleSelector_template("placementFilter", this.getTemplateViewModel(), this.caller);
        this.placementFilter.setLabel("Page Type");
        this.placementFilter.setFilterContainerClass('margin-neg');
        this.placementFilter.setTagContainerId('selected-placements');
        this.placementFilter.setTagContainerCaption('Selected page types:');

        this.spendSourcesFilter = new multipleSelector_template("spendSourcesFilter", this.getTemplateViewModel(), this.caller);
        this.spendSourcesFilter.setLabel("Spend Sources");
        this.spendSourcesFilter.setFilterContainerClass('margin-neg');
        this.spendSourcesFilter.setTagContainerId('selected-spend-sources');
        this.spendSourcesFilter.setTagContainerCaption('Selected spend sources:');

        this.makeFilter = new multipleSelector_template("makeFilter", this.getTemplateViewModel(), this.caller);
        this.makeFilter.setLabel("Make");
        this.makeFilter.setFilterContainerClass('margin-neg');
        this.makeFilter.setTagContainerId('selected-makes');
        this.makeFilter.setTagContainerCaption('Selected makes:');

        this.datesFilter = new filterDate_template("datesFilter", this.getTemplateViewModel(), this.caller);

        let date = new Date();
        date.setHours(0,0,0,0);
        let firstOfMonth = new Date(date.getFullYear(),date.getMonth(), 1);

        // if it is the first day of the month, we display data from the last month
        if(date.getTime() === firstOfMonth.getTime()){
            firstOfMonth = new Date(date.getFullYear(),date.getMonth() - 1, 1);
            date.setDate(date.getDate() - 1);
        }else{
            date.setDate(date.getDate() - 1);
        }

        this.datesFilter.setDateRange(firstOfMonth, date);

        this.validationsList = new validationsList_template("validationList", this.getTemplateViewModel(), this);
        this.exportReportDataCsv = new exportDataCsv();

        // Populate view-mode options after template init; vm build can run before subclass fields are ready.
        this.getTemplateViewModel().viewModes(this.getViewModes());
        this.setViewMode('date');
    }

    protected initializeToolTips(): void {
        if (this.uniqueClicksTooltip === undefined) {
            this.uniqueClicksTooltip = new infoButton_template('uniqueClicksTooltip', this.getTemplateViewModel(), this.caller);
            this.uniqueClicksTooltip.setTitle('Unique Clicks');
        }

        this.uniqueClicksTooltip.setContent("<p>Distinct clicks deduplicated by IP.</p>");

        if (this.monetizedClicksTooltip === undefined) {
            this.monetizedClicksTooltip = new infoButton_template('monetizedClicksTooltip', this.getTemplateViewModel(), this.caller);
            this.monetizedClicksTooltip.setTitle('Monetized Clicks');
        }

        let monetizedClicksTooltipContent = `
            <p>Clicks eligible for payout based on partner conditions:</p>
            <div class="w-mega-tooltip">
                <div class="column">
                    <div class="mega-tooltip-card">
                        <p>
                            <strong class="mega-title">Lotlinx</strong>
                        </p>
                        <ul>
                            <li><p>IF user A clicks VDP 1, AND clicks again, THEN there is one monetizable event.</p></li>
                            <li><p>BUT IF user A clicks VDP 1 in the next calendar month, THEN there is another (new) monetizable event.</p></li>
                        </ul>
                    </div>
                    <div class="mega-tooltip-card">
                        <p>
                            <strong class="mega-title">CarZing</strong>
                        </p>
                        <ul>
                            <li><p>IF user A clicks VDP 1, AND clicks again, THEN there is one monetizable event.</p></li>
                            <li><p>BUT IF user A clicks VDP 1 in the next calendar month, THEN there is another (new) monetizable event.</p></li>
                        </ul>
                    </div>
                    <div class="mega-tooltip-card">
                        <p>
                            <strong class="mega-title">Cars & Bids</strong>
                        </p>
                        <ul>
                            <li><p>Repeat clicks are treated as monetizable as long as they qualify as valid clicks (no cap stated; per-user nor per-dealer).</p></li>
                            <li><p><em>*As long as the repeated clicks are perceived as valid from the partner (non-bot, non-suspicious), payment is received.</em></p></li>
                        </ul>
                    </div>
                </div>
            </div>
            `;

        this.monetizedClicksTooltip.setContent(monetizedClicksTooltipContent);
    }

    protected getTemplateName(): string{
        return "carMarketplaceAffiliateReport_template";
    }

    protected getMainElementClasses(): Array<string> {
        return ["w-content", "_car_marketplace_rev"];
    }

    protected buildViewModel(): any {

        let self = this;

        return {
            reportData              : ko.observableArray([])
            ,columns                : ko.observableArray(this.buildDateViewColumns([], []))
            ,affiliateColumnCount   : ko.observable(this.getAffiliateColumnDefinitions().length)
            ,metricColumnCount      : ko.observable(this.getDateMetricColumns().length)
            ,spendColumnCount       : ko.observable(this.getSpendColumns().length)
            ,providerCompareColumnCount : ko.observable(0)
            ,viewModes              : ko.observableArray([])
            ,viewModeSelected       : ko.observable("date")
            ,viewModeSelectedText   : ko.observable("Date")
            ,trafficTypes           : ko.observableArray([])
            ,trafficTypeSelected    : ko.observable("all")
            ,trafficTypeSelectedText: ko.observable("All")
            ,orderBy                : ko.observable("date")
            ,orderByDirection       : ko.observable("ASC")
            ,orderByAndDirection    : ko.observable("date_ASC")
            ,selectedRows           : ko.observableArray([])
            ,exportButtonHidden     : ko.observable(1)
            ,exportDataButtonClickable: ko.observable(1)
            ,collapseButtonClass    : ko.observable("i-expand")
            ,collapseButtonText     : ko.observable("expand")
            ,uniqueClicksTooltip    : this.uniqueClicksTooltip
            ,monetizedClicksTooltip : this.monetizedClicksTooltip
            ,formatData             : function(value, type){ return self.formatData(value, type); }
            ,onClickReorder         : function(value, data, event){ self.onClickReorder(value, data, event); }
            ,onClickRowClick        : function(index, data){ self.onClickRowClick(index); return true; }
            ,exportData             : function(){ self.exportData(); }
            ,onCollapseClick        : function(){ self.onCollapseClick(); }
            ,onViewModeChange       : function(data){ self.onViewModeChange(data); }
            ,onTrafficTypeChange    : function(data){ self.onTrafficTypeChange(data); }
        };
    }

    public getViewModelForRequest(){
        // Read selector ids once so request/export/preset URL generation share the same values.
        const selectedBrandIds = this.getTemplateViewModel().brandsFilter.selectedItems();
        const selectedAffiliateIds = this.getTemplateViewModel().affiliateFilter.selectedItems();
        const selectedPlacementIds = this.getTemplateViewModel().placementFilter.selectedItems();
        const selectedMakeIds = this.getTemplateViewModel().makeFilter.selectedItems();
        const selectedSpendSourceIds = this.isDateViewMode()
            ? this.getTemplateViewModel().spendSourcesFilter.selectedItems()
            : [];
        const selectedTrafficType = this.isDateViewMode()
            ? this.getTemplateViewModel().trafficTypeSelected()
            : 'all';
        const selectedViewMode = this.getTemplateViewModel().viewModeSelected();
        const sharedFilterState = {
            // View-mode and ordering state first to keep core UI mode resilient in long preset query payloads.
            viewMode: selectedViewMode,
            orderBy: this.getTemplateViewModel().orderBy(),
            orderByDirection: this.getTemplateViewModel().orderByDirection(),
            // Filter selectors.
            brands: selectedBrandIds,
            affiliates: selectedAffiliateIds,
            placements: selectedPlacementIds,
            makes: selectedMakeIds,
            spendSources: selectedSpendSourceIds,
            trafficType: selectedTrafficType,
            // Date-range filter payload.
            dateFilterType: this.getTemplateViewModel().datesFilter.selectedFilter(),
            startDate: this.getTemplateViewModel().datesFilter.startDate(),
            endDate: this.getTemplateViewModel().datesFilter.endDate(),
            startDateRange: this.getTemplateViewModel().datesFilter.fromRange(),
            endDateRange: this.getTemplateViewModel().datesFilter.toRange(),
        };

        // Build one canonical payload shape for both list_data and export calls.
        return {
            ...sharedFilterState,
            // Make/model mode is now filtered by make only.
            filters: { ...sharedFilterState },
        }
    }

    //build the date Picker calendar
    public buildDatePickerCalendar(){

        this.datesFilter.buildCalendar();
    }

    public setBrandFilterItems(departments){

        let groups = [];
        for ( let department of departments){

            let group = {"name" : department.name, "logo" : department.logo, "items" : department.brands};
            groups.push(group);
        }
        this.brandsFilter.setMultiSectionItems(groups);
    }

    public setAffiliateFilterItems(items){
        // Persist raw items in selector UI.
        this.affiliateFilter.setItems(items);
        // Keep a normalized metadata map for dynamic date-view columns.
        this.affiliateColumnDefinitions = this.normalizeAffiliateColumnDefinitions(items);
        // Rebuild table columns when affiliate list changes.
        this.updateColumnsForSelectedAffiliates();
    }

    public setProviderCompareColumnDefinitions(items){
        // Provider compare metadata is backend-driven and normalized once.
        this.providerCompareColumnDefinitions = this.normalizeProviderCompareColumnDefinitions(items);
        // Recompute date-view compare columns after metadata refresh.
        this.updateColumnsForSelectedAffiliates();
    }

    public setPlacementFilterItems(items){

        this.placementFilter.setItems(items);
    }

    public setSpendSourceFilterItems(items){
        this.spendSourcesFilter.setItems(Array.isArray(items) ? items : []);
        this.spendSourceColumnDefinitions = this.normalizeSpendSourceColumnDefinitions(items);
        this.updateColumnsForSelectedAffiliates();
    }

    public setTrafficTypeFilterItems(items){
        const options = Array.isArray(items) ? items : [];
        this.getTemplateViewModel().trafficTypes(options);
        this.setTrafficType(this.getTemplateViewModel().trafficTypeSelected());
    }

    public setMakeFilterItems(items){
        this.makeFilter.setItems(Array.isArray(items) ? items : []);
    }

    public updateColumnsForSelectedAffiliates(): void {
        let selectedAffiliates: Array<string> = [];
        let selectedSpendSources: Array<string> = [];
        const viewMode = this.getSelectedViewMode();

        // Read selected affiliates defensively because view model boots asynchronously.
        if (this.getTemplateViewModel().affiliateFilter && this.getTemplateViewModel().affiliateFilter.selectedItems) {
            selectedAffiliates = this.normalizeIdList(this.getTemplateViewModel().affiliateFilter.selectedItems());
        }

        if (viewMode === 'date'
            && this.getTemplateViewModel().spendSourcesFilter
            && this.getTemplateViewModel().spendSourcesFilter.selectedItems) {
            selectedSpendSources = this.normalizeIdList(this.getTemplateViewModel().spendSourcesFilter.selectedItems());
        }

        // Date view keeps affiliate/compare sections; make views use compact metric-only columns.
        const includeProviderCompare = this.getTemplateViewModel().collapseButtonText() === 'simplify' && viewMode === 'date';
        let columns = [];
        // Route column composition by active mode.
        if (viewMode === 'make') {
            columns = this.buildMakeViewColumns();
        } else if (viewMode === 'make_model') {
            columns = this.buildMakeModelViewColumns();
        } else {
            columns = this.buildDateViewColumns(selectedAffiliates, selectedSpendSources, includeProviderCompare);
        }

        // Section counts drive grouped headers in the date-mode table.
        const affiliateColumnCount = columns.filter((column) => column.group === 'affiliate').length;
        const providerCompareColumnCount = columns.filter((column) => column.group === 'providerCompare').length;
        const metricColumnCount = columns.filter((column) => column.group === 'metrics').length;
        const spendColumnCount = columns.filter((column) => column.group === 'spend').length;

        // Publish the full column state for KO bindings.
        this.getTemplateViewModel().columns(columns);
        this.getTemplateViewModel().affiliateColumnCount(affiliateColumnCount);
        this.getTemplateViewModel().providerCompareColumnCount(providerCompareColumnCount);
        this.getTemplateViewModel().metricColumnCount(metricColumnCount);
        this.getTemplateViewModel().spendColumnCount(spendColumnCount);
    }

    public setReportData(data){
        let reportData = data || [];

        if (this.getSelectedViewMode() === 'make_model') {
            // Preserve grouped visual hierarchy: print make once, then blank for following model/subtotal rows.
            let previousMake = '';
            reportData = reportData.map((row) => {
                const mappedRow = { ...row };
                // Hide repeated make labels on consecutive model rows.
                if (row.rowType === 'model' && previousMake === row.make) {
                    mappedRow.make = '';
                // Keep subtotal rows visually under the current make group.
                } else if (row.rowType === 'make_total') {
                    mappedRow.make = '';
                }

                // Track current make only while traversing model rows.
                if (row.rowType === 'model') {
                    previousMake = row.make || previousMake;
                // Reset group tracking after subtotal so next make starts fresh.
                } else if (row.rowType === 'make_total') {
                    previousMake = '';
                }

                return mappedRow;
            });
        }

        this.getTemplateViewModel().reportData(reportData);

        // Export is only available when the table has visible rows.
        if (reportData.length > 0) {
            this.showExportButton();
        } else {
            this.hideExportButton();
        }
    }

    public setViewModelFromRequest(params : Array<any>): void{
        // Normalize query payload values to stable id arrays before restoring multi-select filters.
        const brandPresetIds = this.normalizePresetIdsForMultiSelector(params['brands']);
        const affiliatePresetIds = this.normalizePresetIdsForMultiSelector(params['affiliates']);
        const placementPresetIds = this.normalizePresetIdsForMultiSelector(params['placements']);
        const spendSourcePresetIds = this.normalizePresetIdsForMultiSelector(params['spendSources']);
        const makePresetIds = this.normalizePresetIdsForMultiSelector(params['makes']);

        // Restore selector filters from URL preset/query payload.
        this.presetFilter.setMultiSelectorPresetFilters(this.brandsFilter, brandPresetIds);
        this.presetFilter.setMultiSelectorPresetFilters(this.affiliateFilter, affiliatePresetIds);
        this.presetFilter.setMultiSelectorPresetFilters(this.placementFilter, placementPresetIds);
        this.presetFilter.setMultiSelectorPresetFilters(this.spendSourcesFilter, spendSourcePresetIds);
        this.presetFilter.setMultiSelectorPresetFilters(this.makeFilter, makePresetIds);
        this.setTrafficType(String(params['trafficType'] ?? 'all'));
        // Restore date filter bounds.
        this.presetFilter.setDates(this.datesFilter, params['dateFilterType'], params['startDate'], params['endDate'], params['startDateRange'], params['endDateRange']);
        // Honor explicit preset mode only; unknown/missing values fall back to safe date defaults.
        this.setViewMode(this.resolveViewModeFromPreset(params['viewMode']));

        if(typeof params['orderBy'] !== 'undefined'){
            // Preserve explicit sort field from preset when provided.
            this.getTemplateViewModel().orderBy(params['orderBy']);
        }

        if(typeof params['orderByDirection'] !== 'undefined'){
            // Preserve explicit sort direction from preset when provided.
            this.getTemplateViewModel().orderByDirection(params['orderByDirection']);
        }

        if(typeof params['orderByAndDirection'] !== 'undefined'){
            // Keep the composed sort token in sync with field+direction.
            this.getTemplateViewModel().orderByAndDirection(params['orderByAndDirection']);
        }

        // Ensure table headers match restored filters/mode.
        this.updateColumnsForSelectedAffiliates();
    }

    private resolveViewModeFromPreset(savedViewMode: any): string {
        const normalizedSavedViewMode = String(savedViewMode ?? '').trim().toLowerCase();
        if (normalizedSavedViewMode === 'date' || normalizedSavedViewMode === 'make' || normalizedSavedViewMode === 'make_model') {
            return normalizedSavedViewMode;
        }

        return 'date';
    }

    private normalizePresetIdsForMultiSelector(rawPresetValue: any): Array<any> {
        if (rawPresetValue === null || typeof rawPresetValue === 'undefined') {
            return [];
        }

        // Query-string truncation can mutate payload shape, so coerce all variants back to id arrays.
        let rawValues: Array<any> = [];

        if (Array.isArray(rawPresetValue)) {
            rawValues = rawPresetValue;
        } else if (typeof rawPresetValue === 'object') {
            rawValues = typeof rawPresetValue.id !== 'undefined'
                ? [rawPresetValue.id]
                : Object.values(rawPresetValue);
        } else if (typeof rawPresetValue === 'string') {
            const trimmedValue = rawPresetValue.trim();
            if (trimmedValue === '') {
                return [];
            }

            try {
                return this.normalizePresetIdsForMultiSelector(JSON.parse(trimmedValue));
            } catch (e) {
                rawValues = trimmedValue.split(',');
            }
        } else {
            rawValues = [rawPresetValue];
        }

        return this.normalizePresetArrayItems(rawValues);
    }

    private normalizePresetArrayItems(rawItems: Array<any>): Array<any> {
        const normalizedItems: Array<any> = [];
        const seenItems = {};

        for (const rawItem of rawItems) {
            if (rawItem === null || typeof rawItem === 'undefined') {
                continue;
            }

            if (Array.isArray(rawItem)) {
                for (const nestedItem of this.normalizePresetArrayItems(rawItem)) {
                    const nestedKey = String(nestedItem);
                    if (!seenItems[nestedKey]) {
                        seenItems[nestedKey] = true;
                        normalizedItems.push(nestedItem);
                    }
                }
                continue;
            }

            const scalarSource = (typeof rawItem === 'object' && typeof rawItem.id !== 'undefined')
                ? rawItem.id
                : rawItem;
            const normalizedScalar = this.normalizePresetScalarValue(scalarSource);
            if (normalizedScalar === null) {
                continue;
            }

            const itemKey = String(normalizedScalar);
            if (seenItems[itemKey]) {
                continue;
            }

            seenItems[itemKey] = true;
            normalizedItems.push(normalizedScalar);
        }

        return normalizedItems;
    }

    private normalizePresetScalarValue(rawValue: any): any | null {
        if (rawValue === null || typeof rawValue === 'undefined') {
            return null;
        }

        if (typeof rawValue === 'number') {
            return rawValue;
        }

        const normalizedValue = String(rawValue).trim().replace(/^["'\[]+|["'\]]+$/g, '');
        if (normalizedValue === '') {
            return null;
        }

        return /^-?\d+$/.test(normalizedValue) ? Number(normalizedValue) : normalizedValue;
    }

    private setTrafficType(trafficType: string): void {
        const normalizedTrafficType = trafficType === 'paid' || trafficType === 'organic' ? trafficType : 'all';
        const options = this.getTemplateViewModel().trafficTypes();
        const selectedOption = Array.isArray(options)
            ? options.find((option) => option.id === normalizedTrafficType)
            : null;

        this.getTemplateViewModel().trafficTypeSelected(normalizedTrafficType);
        this.getTemplateViewModel().trafficTypeSelectedText(selectedOption ? selectedOption.name : 'All');
    }

    private onTrafficTypeChange(selectedTrafficTypeOption): void {
        if (selectedTrafficTypeOption?.name) {
            this.getTemplateViewModel().trafficTypeSelectedText(selectedTrafficTypeOption.name);
        }

        this.caller.getListData();
    }

    private setViewMode(viewMode: string): void {
        // Guard unknown modes by falling back to the production-safe date mode.
        const normalizedViewMode = ['date', 'make', 'make_model'].includes(viewMode) ? viewMode : 'date';
        const selectedViewMode = this.getViewModes().find((mode) => mode.id === normalizedViewMode);
        this.getTemplateViewModel().viewModeSelected(normalizedViewMode);
        this.getTemplateViewModel().viewModeSelectedText(selectedViewMode ? selectedViewMode.name : 'Date');

        // Sorting defaults differ across view modes: date-first for date mode, revenue-first for make modes.
        if (normalizedViewMode === 'date') {
            this.getTemplateViewModel().orderBy("date");
            this.getTemplateViewModel().orderByDirection("ASC");
            this.getTemplateViewModel().orderByAndDirection("date_ASC");
        } else {
            this.getTemplateViewModel().orderBy("totalRevenue");
            this.getTemplateViewModel().orderByDirection("DESC");
            this.getTemplateViewModel().orderByAndDirection("totalRevenue_DESC");
        }

    }

    private onViewModeChange(selectedViewMode): void {
        // Accept both KO option object callback and plain current observable value.
        const nextViewMode = selectedViewMode?.id ?? this.getTemplateViewModel().viewModeSelected();
        this.setViewMode(nextViewMode);

        if (nextViewMode !== 'date') {
            // Compare columns only exist in date mode; reset expand state when leaving it.
            this.getTemplateViewModel().collapseButtonClass('i-expand');
            this.getTemplateViewModel().collapseButtonText('expand');
        }

        this.updateColumnsForSelectedAffiliates();
        this.caller.getListData();
    }

    private formatData(value, type){

        if (type === "date" && value !== null && typeof value !== 'undefined' && value !== 'TOTAL') {
            return new Date(value).toLocaleDateString('en-CA', {
                weekday: 'short',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                timeZone: 'UTC',
            });
        }

        if (type === "numberNullable") {
            if (value === null || typeof value === 'undefined') {
                return '-';
            }
            return Number(value).toLocaleString('en-EG', {minimumFractionDigits: 0, maximumFractionDigits: 0});
        }

        if (type === "decimal" && (value === null || typeof value === 'undefined')) {
            return '-';
        }

        if (type === "currencyNullable") {
            if (value === null || typeof value === 'undefined') {
                return '-';
            }
            return '$' + Number(value).toLocaleString('en-EG', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        }

        if (value === null || typeof value === 'undefined'){
            if (type === "text" || type === "date") {
                return '';
            }
            value = 0;
        }

        if (type === "number") {
            return Number(value).toLocaleString('en-EG', {minimumFractionDigits: 0, maximumFractionDigits: 0});
        }

        if (type === "currency") {
            return '$' + Number(value).toLocaleString('en-EG', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        }

        if (type === "decimal") {
            return Number(value).toLocaleString('en-EG', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        }

        if (type === "percent2") {
            if (value === null || typeof value === 'undefined') {
                return '-';
            }
            return Number(value * 100).toLocaleString('en-EG', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + '%';
        }

        return value;
    }

    public onClickReorder(value, data, event){

        if(typeof data.orderBy === 'undefined'){

            data = this.getTemplateViewModel();
        }

        data.orderBy(value);

        if(data.orderByAndDirection() === null || data.orderByAndDirection().includes(value)){

            if( data.orderByDirection() == "DESC" ){

                data.orderByDirection("ASC");
            }
            else{

                data.orderByDirection("DESC");
            }
        }
        else{

            data.orderByDirection("DESC");
        }

        data.orderByAndDirection(value + "_" + data.orderByDirection());

        this.caller.listReorder();
    }

    private onClickRowClick(index){

        let selectedRows = this.getTemplateViewModel().selectedRows();
        let check = selectedRows.indexOf(index);

        if (check !== -1) {

            selectedRows.splice(check, 1);
        }else {

            selectedRows.push(index);
        }

        this.getTemplateViewModel().selectedRows(selectedRows);
    }

    private onCollapseClick(){
        if (!this.isDateViewMode()) {
            return;
        }

        // "expand" => show compare columns, "simplify" => hide compare columns.
        if(this.getTemplateViewModel().collapseButtonText() == 'simplify'){

            this.getTemplateViewModel().collapseButtonClass('i-expand');
            this.getTemplateViewModel().collapseButtonText('expand');
        }else{

            this.getTemplateViewModel().collapseButtonClass('i-simplify');
            this.getTemplateViewModel().collapseButtonText('simplify');
        }

        this.updateColumnsForSelectedAffiliates();
    }

    public resetSelectedRows(){

        this.getTemplateViewModel().selectedRows([]);
    }

    private exportData() {
        // Prevent duplicate export requests while one export is already running.
        let check = this.getTemplateViewModel().exportDataButtonClickable();
        if (check) {
            this.getTemplateViewModel().exportDataButtonClickable(0);
            this.caller.getCsvExportData();
        }
    }

    public setExportData(reportData){
        let exportDataView = [];

        // Map visible report rows into CSV rows using view-mode aware formatters.
        for (let row of (reportData || [])) {
            exportDataView.push(this.getFormatedExportRow(row));
        }

        // Trigger download and re-enable export button state.
        this.exportReportDataCsv.exportToCsv(this.getExportFileName(), exportDataView);
        this.getTemplateViewModel().exportDataButtonClickable(1);
    }

    /**
     * Returns a CSV-safe header for a table column. Section headers disambiguate duplicate
     * on-screen labels (e.g. affiliate Total vs spend Total) that would collide as object keys.
     */
    private getExportColumnLabel(column: { key: string, label: string }): string {
        if (column.key === 'spendTotal') {
            return 'Total Spend';
        }

        return column.label;
    }

    private getFormatedExportRow(exportDataRow) {
        const viewMode = this.getSelectedViewMode();
        let exportDataViewRow = [];

        if (viewMode === 'make') {
            // Make view export has a fixed compact schema.
            exportDataViewRow["Make"] = this.formatData(exportDataRow.make, "text");
            exportDataViewRow["Total clicks"] = this.formatData(exportDataRow.clicks, "number");
            exportDataViewRow["Monetized clicks"] = this.formatData(exportDataRow.monetizedClicks, "number");
            exportDataViewRow["Revenue"] = this.formatData(exportDataRow.totalRevenue, "currency");
            return exportDataViewRow;
        }

        if (viewMode === 'make_model') {
            // Make+model export preserves grouped dimensions and subtotal rows as provided.
            exportDataViewRow["Make"] = this.formatData(exportDataRow.make, "text");
            exportDataViewRow["Model"] = this.formatData(exportDataRow.model, "text");
            exportDataViewRow["Total clicks"] = this.formatData(exportDataRow.clicks, "number");
            exportDataViewRow["Monetized clicks"] = this.formatData(exportDataRow.monetizedClicks, "number");
            exportDataViewRow["Revenue"] = this.formatData(exportDataRow.totalRevenue, "currency");
            return exportDataViewRow;
        }

        const columns = this.getTemplateViewModel().columns();
        const includeProviderCompare = this.getTemplateViewModel().providerCompareColumnCount() > 0;

        // Walk visible date-view columns in table order so export headers stay aligned with the UI.
        for (const column of columns) {
            if (column.group === 'providerCompare' && !includeProviderCompare) {
                continue;
            }

            const label = this.getExportColumnLabel(column);
            exportDataViewRow[label] = this.formatData(exportDataRow[column.key], column.type);
        }

        return exportDataViewRow;
    }

    private getExportFileName(){
        return "carMarketplaceAffiliate_data_from_" + this.datesFilter.getTemplateViewModel().startDate() + "_to_" + this.datesFilter.getTemplateViewModel().endDate() + ".csv";
    }

    public hideExportButton(){
        this.getTemplateViewModel().exportButtonHidden(1);
    }

    public showExportButton(){
        this.getTemplateViewModel().exportButtonHidden(0);
    }
}



// WEBPACK FOOTER //
// ./js/components/templates/carMarketplaceAffiliateReport_template.ts